import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Container, Typography, Paper, Link, CircularProgress } from "@mui/material";

export default function TermCondition() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const settings = useSelector(state => state.settingsdata.settings);

  const termsUrl: string | null | undefined = settings?.termsOfUse;
  const [iframeLoading, setIframeLoading] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showIframe = !!termsUrl && !iframeError;

  const embedUrl = useMemo(() => {
    if (!termsUrl) return '';
    const clean = termsUrl.split('?')[0].toLowerCase();
    const isDoc = clean.endsWith('.doc') || clean.endsWith('.docx');
    if (isDoc) {
      return `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(termsUrl)}`;
    }
    return termsUrl;
  }, [termsUrl]);

  useEffect(() => {
    if (!termsUrl) {
      setIframeLoading(false);
      setIframeError(false);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      return;
    }

    setIframeError(false);
    setIframeLoading(true);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIframeLoading(current => {
        if (current) setIframeError(true);
        return false;
      });
    }, 15000);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [termsUrl]);

  const handleLoaded = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIframeLoading(false);
  };

  const handleError = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIframeLoading(false);
    setIframeError(true);
  };

  if (showIframe) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100dvh',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        {iframeLoading && (
          <Box
            sx={{
              inset: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2,
              zIndex: 1,
              bgcolor: 'background.paper',
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              {t('loading') || 'Carregando...'}
            </Typography>
          </Box>
        )}
        <iframe
          key={embedUrl}
          src={embedUrl}
          title="Termos de Uso"
          style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
          onLoad={handleLoaded}
          onError={handleError}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100dvh' }}>
      <Container maxWidth="lg">
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 2 }}>
          <Box sx={{ direction: isRTL ? 'rtl' : 'ltr' }}>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                mb: 3,
                textAlign: isRTL ? 'right' : 'left',
                color: 'text.primary'
              }}
            >
              {t('term_condition')}
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{
                textAlign: isRTL ? 'right' : 'left',
                mb: 2,
                lineHeight: 1.8,
                color: 'text.secondary'
              }}
            >
              {t('term_condition_para1')}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                textAlign: isRTL ? 'right' : 'left',
                mt: 2,
                mb: 1,
                color: 'text.primary'
              }}
            >
              {t('term_condition_heading1')}
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{
                textAlign: isRTL ? 'right' : 'left',
                mb: 2,
                lineHeight: 1.8,
                color: 'text.secondary'
              }}
            >
              {t('term_condition_para2')}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                textAlign: isRTL ? 'right' : 'left',
                mt: 2,
                mb: 1,
                color: 'text.primary'
              }}
            >
              {t('term_condition_heading2')}
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{
                textAlign: isRTL ? 'right' : 'left',
                mb: 2,
                lineHeight: 1.8,
                color: 'text.secondary'
              }}
            >
              {t('term_condition_para3')}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                textAlign: isRTL ? 'right' : 'left',
                mt: 2,
                mb: 1,
                color: 'text.primary'
              }}
            >
              {t('term_condition_heading3')}
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{
                textAlign: isRTL ? 'right' : 'left',
                mb: 2,
                lineHeight: 1.8,
                color: 'text.secondary'
              }}
            >
              {t('term_condition_para4')}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                textAlign: isRTL ? 'right' : 'left',
                mt: 2,
                mb: 1,
                color: 'text.primary'
              }}
            >
              {t('term_condition_heading4')}
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{
                textAlign: isRTL ? 'right' : 'left',
                mb: 2,
                lineHeight: 1.8,
                color: 'text.secondary'
              }}
            >
              {t('term_condition_para5')}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                textAlign: isRTL ? 'right' : 'left',
                mt: 2,
                mb: 1,
                color: 'text.primary'
              }}
            >
              {t('term_condition_heading5')}
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{
                textAlign: isRTL ? 'right' : 'left',
                mb: 2,
                lineHeight: 1.8,
                color: 'text.secondary'
              }}
            >
              {t('term_condition_para6')}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                textAlign: isRTL ? 'right' : 'left',
                mt: 2,
                mb: 1,
                color: 'text.primary'
              }}
            >
              {t('term_condition_heading6')}
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{
                textAlign: isRTL ? 'right' : 'left',
                mb: 2,
                lineHeight: 1.8,
                color: 'text.secondary'
              }}
            >
              {t('term_condition_para7')}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                textAlign: isRTL ? 'right' : 'left',
                mt: 2,
                mb: 1,
                color: 'text.primary'
              }}
            >
              {t('term_condition_heading7')}
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{
                textAlign: isRTL ? 'right' : 'left',
                mb: 2,
                lineHeight: 1.8,
                color: 'text.secondary'
              }}
            >
              {t('term_condition_para8')}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                textAlign: isRTL ? 'right' : 'left',
                mt: 2,
                mb: 1,
                color: 'text.primary'
              }}
            >
              {t('term_condition_heading8')}
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{
                textAlign: isRTL ? 'right' : 'left',
                mb: 2,
                lineHeight: 1.8,
                color: 'text.secondary'
              }}
            >
              {t('term_condition_para9')}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                textAlign: isRTL ? 'right' : 'left',
                mt: 2,
                mb: 1,
                color: 'text.primary'
              }}
            >
              {t('term_condition_heading9')}
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{
                textAlign: isRTL ? 'right' : 'left',
                mb: 2,
                lineHeight: 1.8,
                color: 'text.secondary'
              }}
            >
              {t('term_condition_para10')}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                textAlign: isRTL ? 'right' : 'left',
                mt: 2,
                mb: 1,
                color: 'text.primary'
              }}
            >
              {t('term_condition_heading10')}
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{
                textAlign: isRTL ? 'right' : 'left',
                mb: 2,
                lineHeight: 1.8,
                color: 'text.secondary'
              }}
            >
              {t('term_condition_para11')}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                textAlign: isRTL ? 'right' : 'left',
                mt: 2,
                mb: 1,
                color: 'text.primary'
              }}
            >
              {t('term_condition_heading11')}
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{
                textAlign: isRTL ? 'right' : 'left',
                mb: 2,
                lineHeight: 1.8,
                color: 'text.secondary'
              }}
            >
              {t('term_condition_para12')}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                textAlign: isRTL ? 'right' : 'left',
                mt: 2,
                mb: 1,
                color: 'text.primary'
              }}
            >
              {t('term_condition_heading12')}
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{
                textAlign: isRTL ? 'right' : 'left',
                mb: 2,
                lineHeight: 1.8,
                color: 'text.secondary'
              }}
            >
              {t('term_condition_para13')}
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{
                textAlign: isRTL ? 'right' : 'left',
                mb: 2,
                lineHeight: 1.8,
                color: 'text.secondary'
              }}
            >
              {t('term_condition_para14')}
              {settings && settings.contact_email && (
                <Link
                  href={"mailto:" + settings.contact_email}
                  sx={{
                    fontWeight: 'bold',
                    textDecoration: 'none',
                    ml: 0.5,
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {settings.contact_email}
                </Link>
              )}
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}